import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { addSkuVariant, updateSkuVariant } from "../../store/skuVariantStore/skuVariantThunk";

// One editable variant row in the form.
interface VariantRow {
  name: string;
  unit: string;
  qty: string;
}

const emptyRow: VariantRow = { name: "", unit: "", qty: "" };

interface SkuVariantFormProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
  variantId?: string;
}

// Right-side drawer form (same shell as the Location drawer):
// sticky DialogTitle header, scrollable DialogContent, fixed DialogActions footer.
const SkuVariantForm: FC<SkuVariantFormProps> = ({ open, onClose, variantId }) => {
  const dispatch = useAppDispatch();
  const isEditing = !!variantId;

  const { variants, status } = useAppSelector((state) => state.skuVariants);
  const { units } = useAppSelector((state) => state.units);

  const [name, setName] = useState("");
  const [rows, setRows] = useState<VariantRow[]>([{ ...emptyRow }]);

  // Same searchable unit options as the item form's "Units & Packs" selector.
  const unitOptions = useMemo(
    () => (units || []).map((u: any) => ({ label: `${u.name}${u.code ? ` (${u.code})` : ""}`, value: u._id })),
    [units]
  );

  useEffect(() => {
    if (!open) return;
    if (!units || units.length === 0) dispatch(fetchUnits());
  }, [dispatch, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // hydrate (edit) or reset (new) each time the drawer opens
  useEffect(() => {
    if (!open) return;
    if (isEditing && variantId) {
      const data = (variants || []).find((v: any) => v._id === variantId);
      setName(data?.name || "");
      setRows(
        (data?.variants || []).map((v: any) => ({
          name: v.name || "",
          unit: v.unit?._id || v.unit || "",
          qty: v.qty ?? "",
        }))
      );
    } else {
      setName("");
      setRows([{ ...emptyRow }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variantId]);

  const setRow = (index: number, patch: Partial<VariantRow>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, { ...emptyRow }]);
  const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

  const validRows = rows.filter((r) => r.name && r.unit && r.qty !== "");
  const canSave = !!name && validRows.length > 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    const payload = {
      name,
      variants: validRows.map((r) => ({ name: r.name, unit: r.unit, qty: Number(r.qty) })),
    };
    try {
      if (isEditing && variantId) {
        await dispatch(updateSkuVariant({ id: variantId, payload })).unwrap();
      } else {
        await dispatch(addSkuVariant(payload)).unwrap();
      }
      onClose(true);
    } catch (err: any) {
      toast.error(err?.message || "Failed to save variant set");
    }
  };

  const handleClose = () => onClose(false);

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "560px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: "2px 0 0 2px",
          position: "relative",
        }}
        component="form"
        onSubmit={onSubmit}
        noValidate
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "40px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
              {isEditing ? "Edit Variant Set" : "New Variant Set"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 24px 100px", overflowY: "auto", flex: 1 }}>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Set name *"
                fullWidth
                size="small"
                placeholder="e.g. Whisky Pegs"
                InputLabelProps={{ shrink: true }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Variants
              </Typography>
            </Grid>

            {rows.map((row, i) => (
              <Grid item xs={12} key={i}>
                <Grid container spacing={1} sx={{ alignItems: "center" }}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Name"
                      placeholder="e.g. Chota Peg"
                      value={row.name}
                      onChange={(e) => setRow(i, { name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={7} sm={4}>
                    <Autocomplete
                      options={unitOptions}
                      getOptionLabel={(o: any) => o.label || ""}
                      isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
                      value={unitOptions.find((o) => o.value === row.unit) || null}
                      onChange={(_e, v: any) => setRow(i, { unit: v ? v.value : "" })}
                      renderInput={(params) => <TextField {...params} label="Unit" size="small" />}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Qty"
                      value={row.qty}
                      onChange={(e) => setRow(i, { qty: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRow(i)}
                      disabled={rows.length === 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button startIcon={<AddIcon />} size="small" onClick={addRow}>
                Add variant
              </Button>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #c1c5cb",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            padding: "16px 24px",
            margin: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, width: "100%" }}>
            <Button variant="outlined" onClick={handleClose} sx={{ minWidth: "100px", height: "40px" }}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              disabled={!canSave}
              sx={{ minWidth: "140px", height: "40px" }}
            >
              {isEditing ? "Update" : "Create"}
            </LoadingButton>
          </Box>
        </DialogActions>
      </Box>
    </div>
  );
};

export default SkuVariantForm;
